import { fieldKey, useToast } from "@sarva/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { describeProblem, type ProblemView } from "@/shared/api";

/**
 * [D239] A failed action is a red pop-up, raised in one place for every write in the product.
 *
 * Every mutation's failure passes through the query client's mutation cache, so subscribing
 * there covers all of them — a screen built next month included — without each call site
 * remembering to. The error is then marked as *announced*, and `ProblemNotice` renders nothing
 * for an announced error except publishing its field messages to the fields they are about
 * (they appear under each input). A load failure is not a mutation, is never announced, and
 * keeps its in-panel notice with a retry.
 *
 * A mutation can opt out with `meta: { announceError: false }` when its screen answers the
 * failure itself (a conflict dialog, say).
 */
const announced = new WeakSet<object>();

export function isAnnounced(error: unknown): boolean {
  return typeof error === "object" && error !== null && announced.has(error);
}

/** "individual_npi" → "Individual npi"; "lines[0].paid" → "Lines 1 › Paid": a key a person can read. */
function readable(field: string): string {
  return field
    .split(".")
    .map((part) => {
      const words = part
        .replace(/\[(\d+)\]/g, (_, n: string) => ` ${String(Number(n) + 1)}`)
        .replace(/_/g, " ")
        .trim();
      return words.charAt(0).toUpperCase() + words.slice(1);
    })
    .join(" › ");
}

export function describeForToast(view: ProblemView): {
  title: string;
  description: string;
} {
  if (view.kind === "validation" && view.fields.length > 0) {
    // A field on screen shows its own message under the input; the pop-up lists only the rest,
    // so nothing is said twice and nothing is lost.
    const unseen = view.fields.filter(
      (f) =>
        typeof document === "undefined" ||
        document.querySelector(`[data-field-key="${fieldKey(f.field)}"]`) ===
          null,
    );
    if (unseen.length === 0) {
      return {
        title: view.title,
        description: "Check the fields marked in red.",
      };
    }
    const lines = unseen.map((f) => `${readable(f.field)}: ${f.detail}`);
    return {
      title: view.title,
      description: [view.detail, ...lines].join("\n"),
    };
  }
  if (view.kind === "forbidden" && view.missing.length > 0) {
    return {
      title: view.title,
      description: `${view.detail}\nAsk an administrator for ${view.missing.join(", ")}.`,
    };
  }
  if (view.kind === "conflict" && view.specifics !== undefined) {
    return {
      title: view.title,
      description: `${view.detail}\n${view.specifics}`,
    };
  }
  if (
    (view.kind === "server" || view.kind === "unknown") &&
    view.requestId !== null
  ) {
    return {
      title: view.title,
      description: `${view.detail}\nReference ${view.requestId}`,
    };
  }
  return { title: view.title, description: view.detail };
}

export function ActionErrorToaster(): null {
  const client = useQueryClient();
  const { toast, dismiss } = useToast();
  useEffect(() => {
    // Which form (observer) made each attempt, and the pop-up its last failure raised: trying
    // again closes the old pop-up, so a stale refusal never sits beside a fresh result.
    const observerOf = new WeakMap<object, object>();
    const toastOf = new WeakMap<object, string>();
    return client.getMutationCache().subscribe((event) => {
      if (event.type === "observerAdded") {
        observerOf.set(event.mutation, event.observer);
        const open = toastOf.get(event.observer);
        if (open !== undefined) {
          dismiss(open);
          toastOf.delete(event.observer);
        }
        return;
      }
      if (event.type !== "updated" || event.action.type !== "error") return;
      if (event.mutation.meta?.["announceError"] === false) return;
      const error: unknown = event.action.error;
      if (typeof error === "object" && error !== null) announced.add(error);
      const view = describeProblem(error);
      const id = toast({
        tone: "danger",
        persistent: true,
        ...describeForToast(view),
        attributes: {
          "data-problem-kind": view.kind,
          "data-problem-code": view.code,
        },
      });
      const observer = observerOf.get(event.mutation);
      if (observer !== undefined) toastOf.set(observer, id);
    });
  }, [client, toast, dismiss]);
  return null;
}
